/* eslint-disable react/jsx-props-no-spreading */

import { AbsoluteCenter, Box, Divider, HStack, Stack, Text } from '@chakra-ui/react';
import {
  AudienceInstance,
  AudienceType,
  MatchingCriteria,
  Segmentation,
  User,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';

import AssignmentAdditionalAudienceFiltersForm from './AssignmentAdditionalAudienceFiltersForm';
import AssignmentBuilderFormContainer from '../../layout/AssignmentBuilderFormContainer';
import AssignmentBulkImportView from './AssignmentBulkImportView';
import type { AssignmentFormData } from '../../../types/assignmentTypes';
import AssignmentSegmentationTag from '../../lists/list-items/AssignmentSegmentationTag';
import AudienceSearch from '../../../input/AudienceSearch';
import CreateSwitchContainer from '../../../../create/shared/layout/CreateSwitchContainer';
import { LockFilled } from '@/client/components/icons/ContinuIcons';
import { useAdminAssignmentsStore } from '../../../state/adminAssignmentsStore';
import { useBulkImportAudience } from '../../hooks/useBulkImportAudience';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AssignmentAudienceForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { setActiveForm, setAudienceFormVisited } = useAdminAssignmentsStore();
  const { handleFileUpload } = useBulkImportAudience();

  useEffect(() => {
    setAudienceFormVisited(true);
    setActiveForm('audience');
  }, []);

  const {
    watch,
    setValue,
    formState: { errors },
    clearErrors,
  } = useFormContext<AssignmentFormData>();

  const hasError = !!errors?.audience;

  const { audience, simpleType, bulkAudienceImportData, audienceIsAll, settings } = watch();

  const { type } = settings;

  const { usesBulkAudienceImport } = bulkAudienceImportData;

  const isLocked = !!id && simpleType === 'pointInTime';

  useEffect(() => {
    if (audienceIsAll) {
      setValue(
        'audience',
        audience.filter(
          (a) =>
            a.matchingCriteria !== MatchingCriteria.MatchAny &&
            a.matchingCriteria !== MatchingCriteria.MatchAll,
        ),
      );
    }
  }, [audienceIsAll]);

  useEffect(() => {
    const audienceContainsMatchAnyOrMatchAll = audience.some(
      (a) =>
        a.matchingCriteria === MatchingCriteria.MatchAny ||
        a.matchingCriteria === MatchingCriteria.MatchAll,
    );

    if (!audienceContainsMatchAnyOrMatchAll && id && type === WorkflowType.PointInTime) {
      setValue('audienceIsAll', true);
    }

    if (audience.length > 0 && !audienceContainsMatchAnyOrMatchAll) clearErrors('audience');
  }, [audience]);

  const itemIsSelected = (selectedId: string, matchingCriteria: MatchingCriteria) =>
    audience.some((item) => item.id === selectedId && item.matchingCriteria === matchingCriteria);

  const handleSelectItem = (item: User & Segmentation, matchingCriteria: MatchingCriteria) => {
    if (itemIsSelected(item.id, matchingCriteria)) {
      setValue(
        'audience',
        audience.filter((i) => i.id !== item.id || i.matchingCriteria !== matchingCriteria),
      );

      return;
    }

    setValue('audience', [
      ...audience,
      {
        id: item.id,
        matchingCriteria,
        type: item.__typename === 'User' ? AudienceType.User : AudienceType.Segment,
        name: item.fullName || item.name,
        image: item.image,
        segmentationType: item.__typename === 'User' ? 'user' : item.segmentationType,
      },
    ]);
  };

  const handleRemoveItem = (removalItem: AudienceInstance, matchingCriteria: MatchingCriteria) =>
    setValue(
      'audience',
      audience.filter(
        (item) => item.id !== removalItem.id || item.matchingCriteria !== matchingCriteria,
      ),
    );

  const matchAnySelections = audience.filter(
    (item) => item.matchingCriteria === MatchingCriteria.MatchAny,
  );

  const matchAllSelections = audience.filter(
    (item) =>
      item.matchingCriteria === MatchingCriteria.MatchAll &&
      item.type !== AudienceType.HiredDate &&
      item.type !== AudienceType.PartnerType,
  );

  const matchNoneSelections = audience.filter(
    (item) => item.matchingCriteria === MatchingCriteria.MatchNone,
  );

  const matchAnyList = [
    {
      label: `${t('admin.assignments.audience.list.users')} ${t(
        'admin.assignments.audience.list.matchAny',
      )}`,
      items: matchAnySelections.filter((item) => item.segmentationType === 'user'),
      value: 'users',
    },
    {
      label: `${t('admin.assignments.audience.list.locations')} ${t(
        'admin.assignments.audience.list.matchAny',
      )}`,
      items: matchAnySelections.filter((item) => item.segmentationType === 'location'),
    },
    {
      label: `${t('admin.assignments.audience.list.departments')} ${t(
        'admin.assignments.audience.list.matchAny',
      )}`,
      items: matchAnySelections.filter((item) => item.segmentationType === 'department'),
    },
    {
      label: `${t('admin.assignments.audience.list.teams')} ${t(
        'admin.assignments.audience.list.matchAny',
      )}`,
      items: matchAnySelections.filter((item) => item.segmentationType === 'team'),
    },
    {
      label: `${t('admin.assignments.audience.list.grades')} ${t(
        'admin.assignments.audience.list.matchAny',
      )}`,
      items: matchAnySelections.filter((item) => item.segmentationType === 'grade'),
    },
    {
      label: `${t('admin.assignments.audience.list.orgLevels')} ${t(
        'admin.assignments.audience.list.matchAny',
      )}`,
      items: matchAnySelections.filter((item) => item.segmentationType === 'org_level'),
    },
    {
      label: `${t('admin.assignments.audience.list.groups')} ${t(
        'admin.assignments.audience.list.matchAny',
      )}`,
      items: matchAnySelections.filter((item) => item.segmentationType === 'group'),
    },
  ];

  const matchAllList = [
    {
      label: `${t('admin.assignments.audience.list.users')} ${t(
        'admin.assignments.audience.list.matchAll',
      )}`,
      items: matchAllSelections.filter((item) => item.segmentationType === 'user'),
    },
    {
      label: `${t('admin.assignments.audience.list.locations')} ${t(
        'admin.assignments.audience.list.matchAll',
      )}`,
      items: matchAllSelections.filter((item) => item.segmentationType === 'location'),
    },
    {
      label: `${t('admin.assignments.audience.list.departments')} ${t(
        'admin.assignments.audience.list.matchAll',
      )}`,
      items: matchAllSelections.filter((item) => item.segmentationType === 'department'),
    },
    {
      label: `${t('admin.assignments.audience.list.teams')} ${t(
        'admin.assignments.audience.list.matchAll',
      )}`,
      items: matchAllSelections.filter((item) => item.segmentationType === 'team'),
    },
    {
      label: `${t('admin.assignments.audience.list.grades')} ${t(
        'admin.assignments.audience.list.matchAll',
      )}`,
      items: matchAllSelections.filter((item) => item.segmentationType === 'grade'),
    },
    {
      label: `${t('admin.assignments.audience.list.orgLevels')} ${t(
        'admin.assignments.audience.list.matchAll',
      )}`,
      items: matchAllSelections.filter((item) => item.segmentationType === 'org_level'),
    },
    {
      label: `${t('admin.assignments.audience.list.groups')} ${t(
        'admin.assignments.audience.list.matchAll',
      )}`,
      items: matchAllSelections.filter((item) => item.segmentationType === 'group'),
    },
  ];

  const matchNoneList = [
    {
      label: `${t('admin.assignments.audience.list.users')} ${t(
        'admin.assignments.audience.list.matchNone',
      )}`,
      items: matchNoneSelections.filter((item) => item.segmentationType === 'user'),
      value: 'users',
    },
    {
      label: `${t('admin.assignments.audience.list.locations')} ${t(
        'admin.assignments.audience.list.matchNone',
      )}`,
      items: matchNoneSelections.filter((item) => item.segmentationType === 'location'),
    },
    {
      label: `${t('admin.assignments.audience.list.departments')} ${t(
        'admin.assignments.audience.list.matchNone',
      )}`,
      items: matchNoneSelections.filter((item) => item.segmentationType === 'department'),
    },
    {
      label: `${t('admin.assignments.audience.list.teams')} ${t(
        'admin.assignments.audience.list.matchNone',
      )}`,
      items: matchNoneSelections.filter((item) => item.segmentationType === 'team'),
    },

    {
      label: `${t('admin.assignments.audience.list.grades')} ${t(
        'admin.assignments.audience.list.matchNone',
      )}`,
      items: matchNoneSelections.filter((item) => item.segmentationType === 'grade'),
    },
    {
      label: `${t('admin.assignments.audience.list.orgLevels')} ${t(
        'admin.assignments.audience.list.matchNone',
      )}`,
      items: matchNoneSelections.filter((item) => item.segmentationType === 'org_level'),
    },
    {
      label: `${t('admin.assignments.audience.list.groups')} ${t(
        'admin.assignments.audience.list.matchNone',
      )}`,
      items: matchNoneSelections.filter((item) => item.segmentationType === 'group'),
    },
  ];

  return (
    <AssignmentBuilderFormContainer
      headerComponent={
        <Stack spacing={0}>
          <HStack alignItems="center">
            {isLocked && <LockFilled boxSize={6} />}

            <Text fontSize="24px" fontWeight={600}>
              {isLocked
                ? t('admin.assignments.audience.selectAudience.locked')
                : t('admin.assignments.audience.selectAudience')}
            </Text>
          </HStack>

          <Text fontSize="14px" fontWeight={400} color="neutral.800">
            {t('admin.assignments.audience.selectAudience.helpText')}
          </Text>

          {hasError && (
            <Text color="error" fontSize="14px" fontWeight={400}>
              {t('admin.assignments.audience.error')}
            </Text>
          )}
        </Stack>
      }
      formComponent={
        <Stack spacing={6} paddingBottom={6}>
          {simpleType === 'pointInTime' && (
            <CreateSwitchContainer
              name="audienceIsAll"
              label={t('admin.assignments.audience.sendToAll.label')}
              helpText={t('admin.assignments.audience.sendToAll.helpText')}
              tooltipText={t('admin.assignments.audience.sendToAll.tooltipText')}
            />
          )}

          {usesBulkAudienceImport && <AssignmentBulkImportView />}

          {!usesBulkAudienceImport && !audienceIsAll && (
            <>
              <Stack>
                <AudienceSearch
                  label={t('admin.assignments.audience.includeUsersWhoMatchAnyOfTheFollowing')}
                  helpText={t(
                    'admin.assignments.audience.includeUsersWhoMatchAnyOfTheFollowing.helpText',
                  )}
                  handleSelectItem={(item) => handleSelectItem(item, MatchingCriteria.MatchAny)}
                  selectedItems={matchAnySelections}
                  includeUsers
                  hasError={hasError}
                  onFileUpload={!id ? handleFileUpload : undefined}
                  tooltipText={`${t('admin.assignments.audience.matchAny.tooltipText')}`}
                />

                {matchAnyList
                  .filter((segmentationGroup) => segmentationGroup.items.length > 0)
                  .map((segmentationGroup, index) => (
                    <Stack key={segmentationGroup.label}>
                      <Text variant="createLabel" color="neutral.900">
                        {segmentationGroup.label}
                      </Text>

                      <HStack flexWrap="wrap" gap={0}>
                        {segmentationGroup.items.map((item) => (
                          <AssignmentSegmentationTag
                            key={item.id}
                            item={item}
                            handleRemoveItem={(removalItem) =>
                              handleRemoveItem(removalItem, MatchingCriteria.MatchAny)
                            }
                          />
                        ))}
                      </HStack>

                      {index !==
                        matchAnyList.filter((group) => group.items.length > 0).length - 1 && (
                        <Box position="relative">
                          <Divider marginY={2} />

                          <AbsoluteCenter bg="white" px="4">
                            <Text
                              textTransform="uppercase"
                              fontSize="14px"
                              fontWeight={600}
                              color="neutral.500"
                            >
                              {segmentationGroup.value && segmentationGroup.value === 'users'
                                ? t('admin.assignments.audience.list.or')
                                : t('admin.assignments.audience.list.and')}
                            </Text>
                          </AbsoluteCenter>
                        </Box>
                      )}
                    </Stack>
                  ))}
              </Stack>

              <Stack>
                <AudienceSearch
                  label={t('admin.assignments.audience.includeUsersWhoMatchAllOfTheFollowing')}
                  helpText={t(
                    'admin.assignments.audience.includeUsersWhoMatchAllOfTheFollowing.helpText',
                  )}
                  handleSelectItem={(item) => handleSelectItem(item, MatchingCriteria.MatchAll)}
                  selectedItems={matchAllSelections}
                  hasError={hasError}
                  tooltipText={`${t('admin.assignments.audience.matchAll.tooltipText')}`}
                  placeholder={t('admin.assignments.audience.search.placeholder.segmentationsOnly')}
                />

                {matchAllList
                  .filter((segmentationGroup) => segmentationGroup.items.length > 0)
                  .map((segmentationGroup, index) => (
                    <Stack key={segmentationGroup.label}>
                      <Text variant="createLabel" color="neutral.900">
                        {segmentationGroup.label}
                      </Text>

                      <HStack flexWrap="wrap" gap={0}>
                        {segmentationGroup.items.map((item) => (
                          <AssignmentSegmentationTag
                            key={item.id}
                            item={item}
                            handleRemoveItem={(removalItem) =>
                              handleRemoveItem(removalItem, MatchingCriteria.MatchAll)
                            }
                          />
                        ))}
                      </HStack>
                    </Stack>
                  ))}
              </Stack>

              <Divider />
            </>
          )}

          <Stack>
            <AudienceSearch
              label={t('admin.assignments.audience.excludeUsersWhoMatchAnyOfTheFollowing')}
              helpText={t(
                'admin.assignments.audience.excludeUsersWhoMatchAnyOfTheFollowing.helpText',
              )}
              handleSelectItem={(item) => handleSelectItem(item, MatchingCriteria.MatchNone)}
              selectedItems={matchNoneSelections}
              includeUsers
              hasError={hasError}
              tooltipText={`${t('admin.assignments.audience.matchNone.tooltipText')}`}
            />

            {matchNoneList
              .filter((segmentationGroup) => segmentationGroup.items.length > 0)
              .map((segmentationGroup, index) => (
                <Stack key={segmentationGroup.label}>
                  <Text variant="createLabel" color="neutral.900">
                    {segmentationGroup.label}
                  </Text>

                  <HStack flexWrap="wrap" gap={0}>
                    {segmentationGroup.items.map((item) => (
                      <AssignmentSegmentationTag
                        key={item.id}
                        item={item}
                        handleRemoveItem={(removalItem) =>
                          handleRemoveItem(removalItem, MatchingCriteria.MatchNone)
                        }
                      />
                    ))}
                  </HStack>
                </Stack>
              ))}
          </Stack>

          <Divider />

          <AssignmentAdditionalAudienceFiltersForm />
        </Stack>
      }
    />
  );
}
